import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { collection, query, getDocs, getDoc, updateDoc, doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '../../utils/firebase';
import { collectionPath } from '../../utils/firestoreCollections';
import { sanitizeString } from '../../utils/sanitization';
import { useAuth } from '../auth/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { generateSegmentId } from '../../utils/documentIdUtils';
import { logUserAction } from '../../utils/logger';
import { evaluateSegmentRecipients } from '../../utils/segmentEvaluation';
import { canViewAllEmail, scopeEmailAudience } from '../../utils/emailAccess';
import { createCustomerLookup } from '../../utils/reportOwnership';

// Email validation function
function validateEmail(email) {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
}

const SegmentBuilder = ({ segment, onClose, onSaved, user }) => {
  const { currentUser } = useAuth();
  const effectiveUser = user || currentUser;
  const { can } = usePermissions(effectiveUser);
  const viewAllEmail = canViewAllEmail(effectiveUser, can);
  
  const [formState, setFormState] = useState({
    name: '',
    description: '',
    rules: {
      operator: 'AND',
      conditions: []
    },
    segmentType: 'contacts', // 'contacts', 'customers', or 'opportunities'
    ...segment // this will override with segment.segmentType if editing existing
  });

  const [customers, setCustomers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [previewResults, setPreviewResults] = useState([]);
  const [previewCount, setPreviewCount] = useState(0);
  const [previewLimit, setPreviewLimit] = useState(20);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  // Detect segment type based on rules if not explicitly set
  const effectiveSegmentType = useMemo(() => {
    if (formState.segmentType) {
      return formState.segmentType;
    }
    
    // Auto-detect from first rule's field
    if (formState.rules.conditions.length > 0) {
      const firstField = formState.rules.conditions[0].field;
      
      // Check if it's an opportunity field
      if (firstField.startsWith('opp') || firstField.startsWith('leadSource') || firstField.startsWith('stage') || firstField.startsWith('potentialValue')) {
        return 'opportunities';
      }
      // Check if it's a contact field or customer relationship field
      if (firstField.startsWith('contact') || firstField.startsWith('customer.')) {
        return 'contacts';
      }
      // Otherwise it's a customer field
      return 'customers';
    }
    
    // Default to customers for backwards compatibility
    return 'customers';
  }, [formState.segmentType, formState.rules.conditions]);

  // Available fields for segmentation
  const availableFields = useMemo(() => {
    const customerFields = [
      { value: 'customerName', label: 'Customer Name', type: 'text' },
      { value: 'customerStatus', label: 'Customer Status', type: 'select', options: ['Active', 'Lead', 'Inactive', 'Prospect'] },
      { value: 'customerCity', label: 'City', type: 'text' },
      { value: 'customerState', label: 'State', type: 'text' },
      { value: 'customerCountry', label: 'Country', type: 'text' },
      { value: 'businessType', label: 'Business Type', type: 'text' },
      { value: 'businessFocus', label: 'Business Focus', type: 'text' },
      { value: 'customerEmail', label: 'Has Email', type: 'boolean' },
      { value: 'salesReps', label: 'Assigned Sales Rep', type: 'array' },
      { value: 'createdAt', label: 'Date Created', type: 'date' },
      { value: 'lastOrderDate', label: 'Last Order Date', type: 'date' },
      { value: 'totalRevenue', label: 'Total Revenue', type: 'number' }
    ];

    const contactFields = [
      { value: 'contactEmail', label: 'Contact Email', type: 'text' },
      { value: 'contactName', label: 'Contact Name', type: 'text' },
      { value: 'contactTitle', label: 'Contact Title/Role', type: 'text' },
      { value: 'contactPhone', label: 'Contact Phone', type: 'text' },
      { value: 'contactPrimary', label: 'Is Primary Contact', type: 'select', options: ['Yes', 'No'] },
      { value: 'contactFocus', label: 'Contact Focus', type: 'array' }
    ];

    const customerRelationshipFields = [
      { value: 'customer.customerName', label: 'Customer Name', type: 'text' },
      { value: 'customer.customerStatus', label: 'Customer Status', type: 'select', options: ['Active', 'Lead', 'Inactive', 'Prospect'] },
      { value: 'customer.customerCity', label: 'Customer City', type: 'text' },
      { value: 'customer.customerState', label: 'Customer State', type: 'text' },
      { value: 'customer.businessFocus', label: 'Customer Business Focus', type: 'text' },
      { value: 'customer.salesReps', label: 'Customer Sales Rep', type: 'array' }
    ];

    const opportunityFields = [
      { value: 'oppNumber', label: 'Opportunity Number', type: 'text' },
      { value: 'leadSource', label: 'Lead Source', type: 'text' },
      { value: 'stage', label: 'Stage', type: 'select', options: ['New', 'Qualified', 'Quote', 'Negotiation', 'Closed Won', 'Closed Lost'] },
      { value: 'potentialValue', label: 'Potential Value', type: 'number' },
      { value: 'startDate', label: 'Start Date', type: 'date' },
      { value: 'dueDate', label: 'Due Date', type: 'date' },
      { value: 'salesReps', label: 'Assigned Sales Rep', type: 'array' },
      { value: 'customerName', label: 'Customer Name', type: 'text' },
      { value: 'contactName', label: 'Contact Name', type: 'text' },
      { value: 'contactEmail', label: 'Contact Email', type: 'text' },
      { value: 'createdAt', label: 'Date Created', type: 'date' }
    ];

    if (effectiveSegmentType === 'all') {
      return [
        ...customerFields.map(f => ({ ...f, value: `customer__${f.value}`, group: 'Customer' })),
        ...contactFields.map(f => ({ ...f, value: `contact__${f.value}`, group: 'Contact' })),
        ...opportunityFields
          .filter(f => !['customerName', 'contactName', 'contactEmail'].includes(f.value))
          .map(f => ({ ...f, value: `opp__${f.value}`, group: 'Opportunity' })),
      ];
    } else if (effectiveSegmentType === 'opportunities') {
      return opportunityFields;
    } else if (effectiveSegmentType === 'contacts') {
      return [...contactFields, ...customerRelationshipFields];
    } else {
      return customerFields;
    }
  }, [effectiveSegmentType]);

  // Operators based on field type
  const getOperatorsForType = (type) => {
    switch (type) {
      case 'text':
        return [
          { value: 'equals', label: 'Equals' },
          { value: 'not_equals', label: 'Does not equal' },
          { value: 'contains', label: 'Contains' },
          { value: 'not_contains', label: 'Does not contain' },
          { value: 'is_empty', label: 'Is empty' },
          { value: 'is_not_empty', label: 'Is not empty' }
        ];
      case 'number':
        return [
          { value: 'equals', label: 'Equals' },
          { value: 'not_equals', label: 'Does not equal' },
          { value: 'greater_than', label: 'Greater than' },
          { value: 'less_than', label: 'Less than' },
          { value: 'is_empty', label: 'Is empty' },
          { value: 'is_not_empty', label: 'Is not empty' }
        ];
      case 'date':
        return [
          { value: 'after', label: 'After' },
          { value: 'before', label: 'Before' },
          { value: 'is_empty', label: 'Is empty' },
          { value: 'is_not_empty', label: 'Is not empty' }
        ];
      case 'boolean':
        return [
          { value: 'is_empty', label: 'Is empty' },
          { value: 'is_not_empty', label: 'Is not empty' }
        ];
      case 'select':
        return [
          { value: 'equals', label: 'Equals' },
          { value: 'not_equals', label: 'Does not equal' }
        ];
      case 'array':
        return [
          { value: 'contains', label: 'Contains' },
          { value: 'not_contains', label: 'Does not contain' },
          { value: 'is_empty', label: 'Is empty' },
          { value: 'is_not_empty', label: 'Is not empty' }
        ];
      default:
        return [];
    }
  };

  // Load customers for preview
  useEffect(() => {
    let active = true;
    setCustomers([]);
    setContacts([]);
    setOpportunities([]);
    const loadCustomers = async () => {
      try {
        const [customersSnap, contactsSnap, orderSummaryCacheDoc, oppsSnap] = await Promise.all([
          getDocs(query(collection(db, collectionPath('customers')))),
          getDocs(query(collection(db, collectionPath('contacts')))),
          getDoc(doc(db, collectionPath('reportsCache'), 'customer_order_summary')),
          getDocs(collection(db, collectionPath('opportunities')))
        ]);
        let customersData = customersSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Enrich customers with lastOrderDate and totalRevenue from cached order summary
        // Cache is generated by Cloud Function every 8 hours (1 Firestore read vs 30K+ order reads)
        const orderSummary = orderSummaryCacheDoc.exists() ? (orderSummaryCacheDoc.data().customers || {}) : {};

        customersData = customersData.map(customer => {
          const acct = String(customer.customerAccountNumber || customer.id);
          const summary = orderSummary[acct];
          return {
            ...customer,
            lastOrderDate: customer.lastOrderDate || (summary?.lastOrderDate ?? null),
            totalRevenue: customer.totalRevenue ?? (summary?.totalRevenue || 0),
            totalOrders: customer.totalOrders ?? (summary?.totalOrders || 0)
          };
        });
        
        const scoped = scopeEmailAudience({
          customers: customersData,
          contacts: contactsSnap.docs.map(d => ({ ...d.data(), id: d.id })),
          opportunities: oppsSnap.docs.map(d => ({ ...d.data(), id: d.id })),
        }, effectiveUser, viewAllEmail);
        if (!active) return;
        customersData = scoped.customers;
        setCustomers(scoped.customers);
        setOpportunities(scoped.opportunities);
        
        // Build customer lookup maps for contact enrichment
        const customerLookup = createCustomerLookup(customersData);
        
        // Extract contacts from contacts collection (not deprecated customer.contacts array)
        const extractedContacts = scoped.contacts.map(contact => {
          const customer = customerLookup.forAccount(contact.customerId) || {};
          
          let contactName = 'Unnamed Contact';
          const firstName = contact.firstName?.trim() || contact.contactFirstName?.trim() || '';
          const lastName = contact.lastName?.trim() || contact.contactLastName?.trim() || '';
          
          if (firstName && lastName) {
            contactName = `${firstName} ${lastName}`;
          } else if (firstName) {
            contactName = firstName;
          } else if (lastName) {
            contactName = lastName;
          }
          
          return {
            ...contact,
            id: contact.id,
            customerId: contact.customerId,
            customer: customer,
            contactName: contactName,
            contactTitle: contact.contactPosition || contact.title || '',
            contactEmail: contact.email || contact.contactEmail || '',
            contactPhone: contact.phone || contact.contactPhone || '',
            contactPrimary: (contact.isPrimary === true || contact.isPrimary === 1 || contact.isPrimary === '1' || contact.contactIsMainContact === true || contact.contactIsMainContact === 1 || contact.contactIsMainContact === '1') ? 'Yes' : 'No'
          };
        });
        
        setContacts(extractedContacts);
      } catch (err) {
        console.error('Failed to load customers:', err);
        if (active) setError(`Could not load audience: ${err.message}`);
      }
    };

    loadCustomers();
    return () => { active = false; };
  }, [effectiveUser, viewAllEmail]);

  // Debounced preview calculation
  const previewTimeoutRef = useRef(null);

  const calculatePreview = useCallback(() => {
    const audienceSegment = { segmentType: effectiveSegmentType, rules: formState.rules };
    const data = { contacts, customers, opportunities };
    const raw = evaluateSegmentRecipients(audienceSegment, data);
    // Deduplicate by email — same as CampaignBuilder does via evaluateAllSegments
    const unique = Array.from(
      new Map(raw.map(r => [r.contactEmail.toLowerCase(), r])).values()
    );
    setPreviewCount(unique.length);
    setPreviewResults(unique);
    setPreviewLimit(20);
  }, [effectiveSegmentType, contacts, customers, opportunities, formState.rules]);

  // Auto-preview when rules change (with debouncing)
  useEffect(() => {
    // Clear existing timeout
    if (previewTimeoutRef.current) {
      clearTimeout(previewTimeoutRef.current);
    }

    // Check if we have valid conditions (with values)
    const hasValidConditions = formState.rules.conditions.length > 0 && 
      formState.rules.conditions.some(c => {
        // Operators that don't need values
        if (['is_empty', 'is_not_empty'].includes(c.operator)) {
          return true;
        }
        // Others need a value
        return c.value !== '' && c.value !== null && c.value !== undefined;
      });

    if (hasValidConditions) {
      // Debounce the preview calculation by 300ms
      previewTimeoutRef.current = setTimeout(() => {
        calculatePreview();
      }, 300);
    } else {
      // No valid conditions - calculate total recipients based on mode immediately
      if (effectiveSegmentType === 'contacts' || effectiveSegmentType === 'all') {
        // Contacts/All mode: show all contacts with valid emails
        const validContacts = contacts.filter(c => c.contactEmail && validateEmail(c.contactEmail));
        setPreviewCount(validContacts.length);
      } else {
        // Customers mode: count all contacts with valid emails + fallbacks for customers without contacts
        const validContacts = contacts.filter(c => c.contactEmail && validateEmail(c.contactEmail));
        let totalRecipients = validContacts.length;
        
        // Add fallback count for customers without contacts (only if customer email is valid)
        const customersWithoutContacts = customers.filter(customer => {
          const hasContacts = contacts.some(c => c.customerId === customer.id);
          return !hasContacts && customer.customerEmail && validateEmail(customer.customerEmail);
        });
        
        totalRecipients += customersWithoutContacts.length;
        console.log('Customers mode - no rules, total recipients:', totalRecipients, '(valid contacts:', validContacts.length, ', valid fallbacks:', customersWithoutContacts.length, ')');
        setPreviewCount(totalRecipients);
      }
      setPreviewResults([]);
    }

    // Cleanup timeout on unmount
    return () => {
      if (previewTimeoutRef.current) {
        clearTimeout(previewTimeoutRef.current);
      }
    };
  }, [formState.rules, effectiveSegmentType, customers, contacts, calculatePreview]);

  const handleInputChange = (field, value) => {
    // If changing segmentType, reset conditions to avoid field mismatch
    if (field === 'segmentType' && value !== formState.segmentType) {
      setFormState(prev => ({ 
        ...prev, 
        [field]: value,
        rules: {
          ...prev.rules,
          conditions: [] // Clear conditions when switching types
        }
      }));
      setError('');
      return;
    }
    
    setFormState(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleRulesChange = (rules) => {
    setFormState(prev => ({ ...prev, rules }));
    setError('');
  };

  const addCondition = () => {
    const firstField = availableFields[0];
    const newCondition = {
      field: firstField.value,
      operator: getOperatorsForType(firstField.type)[0]?.value || 'contains',
      value: '',
      logicalOperator: 'AND' // New: each condition has its own logical operator
    };

    handleRulesChange({
      ...formState.rules,
      conditions: [...formState.rules.conditions, newCondition]
    });
  };

  const updateCondition = (index, field, value) => {
    const updatedConditions = [...formState.rules.conditions];
    updatedConditions[index] = {
      ...updatedConditions[index],
      [field]: value
    };

    // Reset operator if field type changed
    if (field === 'field') {
      const fieldDef = availableFields.find(f => f.value === value);
      const operators = getOperatorsForType(fieldDef?.type || 'text');
      updatedConditions[index].operator = operators[0]?.value || 'equals';
      updatedConditions[index].value = '';
    }

    handleRulesChange({
      ...formState.rules,
      conditions: updatedConditions
    });
  };

  const removeCondition = (index) => {
    const updatedConditions = formState.rules.conditions.filter((_, i) => i !== index);
    handleRulesChange({
      ...formState.rules,
      conditions: updatedConditions
    });
  };

  const validateForm = () => {
    if (!formState.name?.trim()) {
      setError('Segment name is required');
      return false;
    }
    if (formState.rules.conditions.length === 0) {
      setError('At least one condition is required');
      return false;
    }
    
    // Validate each condition has a value (unless operator is is_empty/is_not_empty)
    for (const condition of formState.rules.conditions) {
      if (!['is_empty', 'is_not_empty'].includes(condition.operator)) {
        if (!condition.value && condition.value !== 0) {
          setError('All conditions must have a value');
          return false;
        }
      }
    }

    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    setError('');

    try {
      // Recalculate recipient count immediately before saving to ensure accuracy
      let finalRecipientCount = 0;
      
      const audienceSegment = { segmentType: effectiveSegmentType, rules: formState.rules };
      const data = { contacts, customers, opportunities };
      const raw = evaluateSegmentRecipients(audienceSegment, data);
      // Deduplicate by email — same as CampaignBuilder
      const unique = new Map(raw.map(r => [r.contactEmail.toLowerCase(), r]));
      finalRecipientCount = unique.size;
      
      const sanitized = {
        name: sanitizeString(formState.name, 200),
        description: sanitizeString(formState.description, 500),
        segmentType: effectiveSegmentType, // Use the effective type based on rules
        rules: formState.rules,
        contactCount: finalRecipientCount,
        lastCalculated: serverTimestamp(),
        updatedBy: effectiveUser?.uid || '',
        updatedByName: effectiveUser?.displayName || effectiveUser?.email || 'Unknown',
        updatedAt: serverTimestamp()
      };

      if (segment?.id) {
        // Update existing segment
        await updateDoc(
          doc(db, collectionPath('emailSegments'), segment.id),
          sanitized
        );
        await logUserAction({ action: 'Email Segment Updated', details: `Segment updated: "${formState.name}"`, entityType: 'EmailSegment', entityId: segment.id });
        onSaved('Segment updated successfully');
      } else {
        // Create new segment
        const segmentId = generateSegmentId(formState.name);
        await setDoc(
          doc(db, collectionPath('emailSegments'), segmentId),
          {
            ...sanitized,
            isArchived: false,
            archivedAt: null,
            createdBy: effectiveUser?.uid || '',
            createdByName: effectiveUser?.displayName || effectiveUser?.email || 'Unknown',
            createdAt: serverTimestamp()
          }
        );
        await logUserAction({ action: 'Email Segment Created', details: `Segment created: "${formState.name}"`, entityType: 'EmailSegment' });
        onSaved('Segment created successfully');
      }
    } catch (err) {
      console.error('Save error:', err);
      setError('Failed to save segment: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const renderConditionRow = (condition, index) => {
    const fieldDef = availableFields.find(f => f.value === condition.field);
    const operators = getOperatorsForType(fieldDef?.type || 'text');
    const needsValue = !['is_empty', 'is_not_empty'].includes(condition.operator);

    return (
      <div key={index} className="flex items-start space-x-2 bg-gray-50 p-3 rounded-lg">
        <div className="flex-grow grid grid-cols-3 gap-2">
          {/* Field Selector */}
          <select
            value={condition.field}
            onChange={(e) => updateCondition(index, 'field', e.target.value)}
            className="border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          >
            {effectiveSegmentType === 'all' ? (
              ['Customer', 'Contact', 'Opportunity'].map(group => (
                <optgroup key={group} label={group}>
                  {availableFields.filter(f => f.group === group).map(field => (
                    <option key={field.value} value={field.value}>{field.label}</option>
                  ))}
                </optgroup>
              ))
            ) : (
              availableFields.map(field => (
                <option key={field.value} value={field.value}>
                  {field.label}
                </option>
              ))
            )}
          </select>

          {/* Operator Selector */}
          <select
            value={condition.operator}
            onChange={(e) => updateCondition(index, 'operator', e.target.value)}
            className="border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          >
            {operators.map(op => (
              <option key={op.value} value={op.value}>
                {op.label}
              </option>
            ))}
          </select>

          {/* Value Input */}
          {needsValue && (
            <>
              {fieldDef?.type === 'select' ? (
                <select
                  value={condition.value}
                  onChange={(e) => updateCondition(index, 'value', e.target.value)}
                  className="border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="">Select...</option>
                  {fieldDef.options.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              ) : fieldDef?.type === 'date' ? (
                <input
                  type="date"
                  value={condition.value}
                  onChange={(e) => updateCondition(index, 'value', e.target.value)}
                  className="border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              ) : fieldDef?.type === 'number' ? (
                <input
                  type="number"
                  value={condition.value}
                  onChange={(e) => updateCondition(index, 'value', e.target.value)}
                  className="border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="Enter value"
                />
              ) : (
                <input
                  type="text"
                  value={condition.value}
                  onChange={(e) => updateCondition(index, 'value', e.target.value)}
                  className="border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="Enter value"
                />
              )}
            </>
          )}

          {!needsValue && <div className="text-sm text-gray-500 flex items-center px-3">No value needed</div>}
        </div>

        {/* Remove Button */}
        <button
          onClick={() => removeCondition(index)}
          className="px-3 py-2 text-red-600 hover:bg-red-50 rounded transition-colors"
          title="Remove condition"
        >
          ×
        </button>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="border-b px-6 py-4 flex items-center justify-between">
          <h3 className="text-xl font-bold text-gray-900">
            {segment?.id ? 'Edit Segment' : 'Create Segment'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="flex-grow overflow-y-auto px-6 py-4">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {/* Basic Info */}
          <div className="space-y-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Segment Name *
              </label>
              <input
                type="text"
                className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                value={formState.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="e.g., Active Golf Pro Shops"
                maxLength={200}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                value={formState.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Describe this audience segment..."
                rows={2}
                maxLength={500}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Target Type *
              </label>
              <div className="space-y-3">
                <label className={`flex items-start p-3 border rounded cursor-pointer hover:bg-gray-50 transition-colors ${effectiveSegmentType === 'contacts' ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200'}`}>
                  <input
                    type="radio"
                    name="segmentType"
                    value="contacts"
                    checked={effectiveSegmentType === 'contacts'}
                    onChange={(e) => handleInputChange('segmentType', e.target.value)}
                    className="mt-1 mr-3"
                  />
                  <div>
                    <div className="font-medium text-gray-900">Contacts (Individual Recipients)</div>
                    <div className="text-sm text-gray-600">
                      Target individual people within customers. Best for personalized messaging, role-based targeting, and detailed audience segmentation.
                    </div>
                  </div>
                </label>

                <label className={`flex items-start p-3 border rounded cursor-pointer hover:bg-gray-50 transition-colors ${effectiveSegmentType === 'customers' ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200'}`}>
                  <input
                    type="radio"
                    name="segmentType"
                    value="customers"
                    checked={effectiveSegmentType === 'customers'}
                    onChange={(e) => handleInputChange('segmentType', e.target.value)}
                    className="mt-1 mr-3"
                  />
                  <div>
                    <div className="font-medium text-gray-900">Customers (with contacts as recipients)</div>
                    <div className="text-sm text-gray-600">
                      Target customers using their primary email. Includes all customer contacts AND customer emails; uses customer email as fallback if no contacts exist.
                    </div>
                  </div>
                </label>

                <label className={`flex items-start p-3 border rounded cursor-pointer hover:bg-gray-50 transition-colors ${effectiveSegmentType === 'opportunities' ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200'}`}>
                  <input
                    type="radio"
                    name="segmentType"
                    value="opportunities"
                    checked={effectiveSegmentType === 'opportunities'}
                    onChange={(e) => handleInputChange('segmentType', e.target.value)}
                    className="mt-1 mr-3"
                  />
                  <div>
                    <div className="font-medium text-gray-900">Opportunities</div>
                    <div className="text-sm text-gray-600">
                      Target active opportunities based on stage, value, dates, or assigned reps. Send emails to opportunity contacts or customer emails.
                    </div>
                  </div>
                </label>

                <label className={`flex items-start p-3 border rounded cursor-pointer hover:bg-gray-50 transition-colors ${effectiveSegmentType === 'all' ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200'}`}>
                  <input
                    type="radio"
                    name="segmentType"
                    value="all"
                    checked={effectiveSegmentType === 'all'}
                    onChange={(e) => handleInputChange('segmentType', e.target.value)}
                    className="mt-1 mr-3"
                  />
                  <div>
                    <div className="font-medium text-gray-900">All (Cross-Collection)</div>
                    <div className="text-sm text-gray-600">
                      Combine filters from Customers, Contacts, and Opportunities in a single segment. Match contacts that meet criteria across all three collections. Best for advanced audience targeting.
                    </div>
                  </div>
                </label>
              </div>
            </div>
          </div>

          {/* Rule Builder */}
          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold text-gray-900">Segment Rules</h4>
              <span className="text-xs text-gray-500">Choose AND/OR between each condition</span>
            </div>

            {/* Conditions */}
            <div className="space-y-3 mb-4">
              {formState.rules.conditions.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg text-gray-500">
                  No conditions yet. Add a condition to start building your segment.
                </div>
              ) : (
                formState.rules.conditions.map((condition, index) => (
                  <div key={index}>
                    {index > 0 && (
                      <div className="flex items-center justify-center my-2">
                        <button
                          onClick={() => {
                            const updatedConditions = [...formState.rules.conditions];
                            updatedConditions[index].logicalOperator = 'AND';
                            handleRulesChange({ ...formState.rules, conditions: updatedConditions });
                          }}
                          className={`px-4 py-1.5 rounded-l-md text-sm font-semibold transition-colors ${
                            condition.logicalOperator === 'AND'
                              ? 'bg-indigo-600 text-white'
                              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          }`}
                        >
                          AND
                        </button>
                        <button
                          onClick={() => {
                            const updatedConditions = [...formState.rules.conditions];
                            updatedConditions[index].logicalOperator = 'OR';
                            handleRulesChange({ ...formState.rules, conditions: updatedConditions });
                          }}
                          className={`px-4 py-1.5 rounded-r-md text-sm font-semibold transition-colors ${
                            condition.logicalOperator === 'OR'
                              ? 'bg-indigo-600 text-white'
                              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          }`}
                        >
                          OR
                        </button>
                      </div>
                    )}
                    {renderConditionRow(condition, index)}
                  </div>
                ))
              )}
            </div>

            <button
              onClick={addCondition}
              className="w-full px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-indigo-400 hover:text-indigo-600 transition-colors"
            >
              + Add Condition
            </button>
          </div>

          {/* Preview */}
          <div className="border-t pt-4 mt-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold text-gray-900">
                Preview Results
                <span className="ml-2 text-indigo-600 font-bold">
                  ({previewCount.toLocaleString()} contacts)
                </span>
              </h4>
              <button
                onClick={() => setShowPreview(!showPreview)}
                className="text-sm text-indigo-600 hover:text-indigo-700"
              >
                {showPreview ? 'Hide' : 'Show'} Preview
              </button>
            </div>

            {showPreview && (
              <div className="bg-gray-50 rounded-lg border border-gray-200">
                {previewResults.length === 0 ? (
                  <div className="text-center text-gray-500 py-6">
                    No contacts match these criteria
                  </div>
                ) : (
                  <>
                    <p className="text-xs text-gray-600 px-4 pt-3 pb-2">
                      Showing {Math.min(previewLimit, previewResults.length)} of {previewCount.toLocaleString()} recipients:
                    </p>
                    <div className="max-h-80 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-100 sticky top-0">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">Name</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">Email</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">Customer</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white">
                          {previewResults.slice(0, previewLimit).map((contact, idx) => (
                            <tr key={contact.id || idx} className="hover:bg-gray-50">
                              <td className="px-3 py-2 text-gray-900">
                                {contact.contactName || 'Unnamed Contact'}
                                {contact.isFallback && <span className="ml-1 text-xs text-orange-500">(fallback)</span>}
                              </td>
                              <td className="px-3 py-2 text-gray-600">
                                {contact.contactEmail}
                              </td>
                              <td className="px-3 py-2 text-gray-700">
                                {contact.customer?.customerName || contact.customerName || 'N/A'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {previewLimit < previewResults.length && (
                      <div className="text-center py-3 border-t">
                        <button
                          type="button"
                          onClick={() => setPreviewLimit(prev => prev + 50)}
                          className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                        >
                          Load More ({Math.min(50, previewResults.length - previewLimit)} more)
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t px-6 py-4 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            {previewCount > 0 && (
              <>
                <span className="font-medium text-indigo-600">
                  {previewCount.toLocaleString()} recipient{previewCount !== 1 ? 's' : ''} will receive emails sent to this segment
                </span>
                <span className="text-gray-500 ml-2">(with valid email addresses)</span>
              </>
            )}
          </div>
          
          <div className="flex space-x-2">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : segment?.id ? 'Update Segment' : 'Create Segment'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper function to apply contact segment rules (enriches contacts with customer data)
function applyContactSegmentRules(contacts, customers, rules) {
  if (!rules || !rules.conditions || rules.conditions.length === 0) {
    return contacts;
  }

  // Create customer lookup map for O(1) access
  const customersById = new Map(customers.map(c => [c.id, c]));

  // Enrich contacts with customer data
  const enrichedContacts = contacts.map(contact => ({
    ...contact,
    customer: customersById.get(contact.customerId) || {},
    contactPrimary: (contact.isPrimary === true || contact.isPrimary === 1 || contact.isPrimary === '1' || contact.contactIsMainContact === true || contact.contactIsMainContact === 1 || contact.contactIsMainContact === '1') ? 'Yes' : 'No'
  }));

  // Filter enriched contacts using rules (reuse same logic as applySegmentRules)
  return enrichedContacts.filter(contact => {
    const results = rules.conditions.map(condition => {
      const fieldValue = getNestedValue(contact, condition.field);

      switch (condition.operator) {
        case 'equals':
          // Case-insensitive comparison for text fields
          if (typeof fieldValue === 'string' && typeof condition.value === 'string') {
            return fieldValue.toLowerCase() === condition.value.toLowerCase();
          }
          return fieldValue === condition.value;
        case 'not_equals':
          // Case-insensitive comparison for text fields
          if (typeof fieldValue === 'string' && typeof condition.value === 'string') {
            return fieldValue.toLowerCase() !== condition.value.toLowerCase();
          }
          return fieldValue !== condition.value;
        case 'contains':
          if (Array.isArray(fieldValue)) {
            // Special handling for salesReps array (contains objects with salesRepName)
            if (condition.field === 'customer.salesReps') {
              return fieldValue.some(item => 
                item.salesRepName?.toLowerCase().includes(String(condition.value || '').toLowerCase())
              );
            }
            // Generic array handling
            return fieldValue.some(item => 
              String(item).toLowerCase().includes(String(condition.value || '').toLowerCase())
            );
          }
          return String(fieldValue || '').toLowerCase().includes(String(condition.value || '').toLowerCase());
        case 'not_contains':
          if (Array.isArray(fieldValue)) {
            // Special handling for salesReps array
            if (condition.field === 'customer.salesReps') {
              return !fieldValue.some(item => 
                item.salesRepName?.toLowerCase().includes(String(condition.value || '').toLowerCase())
              );
            }
            // Generic array handling
            return !fieldValue.some(item => 
              String(item).toLowerCase().includes(String(condition.value || '').toLowerCase())
            );
          }
          return !String(fieldValue || '').toLowerCase().includes(String(condition.value || '').toLowerCase());
        case 'after': {
          const afterTarget = new Date(condition.value);
          const afterField = fieldValue?.toDate ? fieldValue.toDate() : new Date(fieldValue);
          return afterField > afterTarget;
        }
        case 'before': {
          const beforeTarget = new Date(condition.value);
          const beforeField = fieldValue?.toDate ? fieldValue.toDate() : new Date(fieldValue);
          return beforeField < beforeTarget;
        }
        case 'greater_than':
          return Number(fieldValue) > Number(condition.value);
        case 'less_than':
          return Number(fieldValue) < Number(condition.value);
        case 'is_empty':
          return !fieldValue || (Array.isArray(fieldValue) && fieldValue.length === 0);
        case 'is_not_empty':
          return fieldValue && (!Array.isArray(fieldValue) || fieldValue.length > 0);
        default:
          return false;
      }
    });

    if (rules.operator === 'AND') {
      return results.every(r => r);
    } else {
      return results.some(r => r);
    }
  });
}

// Helper function to apply segment rules
function applySegmentRules(customers, rules) {
  if (!rules || !rules.conditions || rules.conditions.length === 0) {
    return customers;
  }

  return customers.filter(customer => {
    // Evaluate each condition
    const results = rules.conditions.map(condition => {
      const fieldValue = getNestedValue(customer, condition.field);

      switch (condition.operator) {
        case 'equals':
          // Case-insensitive comparison for text fields
          if (typeof fieldValue === 'string' && typeof condition.value === 'string') {
            return fieldValue.toLowerCase() === condition.value.toLowerCase();
          }
          return fieldValue === condition.value;
        case 'not_equals':
          // Case-insensitive comparison for text fields
          if (typeof fieldValue === 'string' && typeof condition.value === 'string') {
            return fieldValue.toLowerCase() !== condition.value.toLowerCase();
          }
          return fieldValue !== condition.value;
        case 'contains':
          if (Array.isArray(fieldValue)) {
            // Special handling for salesReps array (contains objects with salesRepName)
            if (condition.field === 'salesReps') {
              return fieldValue.some(item => 
                item.salesRepName?.toLowerCase().includes(String(condition.value || '').toLowerCase())
              );
            }
            // Generic array handling
            return fieldValue.some(item => 
              String(item).toLowerCase().includes(String(condition.value || '').toLowerCase())
            );
          }
          return String(fieldValue || '').toLowerCase().includes(String(condition.value || '').toLowerCase());
        case 'not_contains':
          if (Array.isArray(fieldValue)) {
            // Special handling for salesReps array
            if (condition.field === 'salesReps') {
              return !fieldValue.some(item => 
                item.salesRepName?.toLowerCase().includes(String(condition.value || '').toLowerCase())
              );
            }
            // Generic array handling
            return !fieldValue.some(item => 
              String(item).toLowerCase().includes(String(condition.value || '').toLowerCase())
            );
          }
          return !String(fieldValue || '').toLowerCase().includes(String(condition.value || '').toLowerCase());
        case 'after': {
          const afterTarget = new Date(condition.value);
          const afterField = fieldValue?.toDate ? fieldValue.toDate() : new Date(fieldValue);
          return afterField > afterTarget;
        }
        case 'before': {
          const beforeTarget = new Date(condition.value);
          const beforeField = fieldValue?.toDate ? fieldValue.toDate() : new Date(fieldValue);
          return beforeField < beforeTarget;
        }
        case 'greater_than':
          return Number(fieldValue) > Number(condition.value);
        case 'less_than':
          return Number(fieldValue) < Number(condition.value);
        case 'is_empty':
          return !fieldValue || (Array.isArray(fieldValue) && fieldValue.length === 0);
        case 'is_not_empty':
          return fieldValue && (!Array.isArray(fieldValue) || fieldValue.length > 0);
        default:
          return false;
      }
    });

    // Apply logical operators per condition
    // First condition is always included
    if (results.length === 0) return false;
    if (results.length === 1) return results[0];
    
    // Build expression: result[0] OPERATOR[1] result[1] OPERATOR[2] result[2]...
    let finalResult = results[0];
    
    for (let i = 1; i < results.length; i++) {
      const condition = rules.conditions[i];
      const logicalOp = condition.logicalOperator || 'AND'; // Default to AND for backwards compatibility
      
      if (logicalOp === 'OR') {
        finalResult = finalResult || results[i];
      } else {
        finalResult = finalResult && results[i];
      }
    }
    
    return finalResult;
  });
}

function getNestedValue(obj, path) {
  return path.split('.').reduce((acc, part) => acc && acc[part], obj);
}

// Helper function to apply segment rules to opportunities
function applyOpportunitySegmentRules(opportunities, rules) {
  if (!rules || !rules.conditions || rules.conditions.length === 0) {
    return opportunities;
  }

  return opportunities.filter(opp => {
    const results = rules.conditions.map(condition => {
      const fieldValue = getNestedValue(opp, condition.field);

      switch (condition.operator) {
        case 'equals':
          if (typeof fieldValue === 'string' && typeof condition.value === 'string') {
            return fieldValue.toLowerCase() === condition.value.toLowerCase();
          }
          return fieldValue === condition.value;
        case 'not_equals':
          if (typeof fieldValue === 'string' && typeof condition.value === 'string') {
            return fieldValue.toLowerCase() !== condition.value.toLowerCase();
          }
          return fieldValue !== condition.value;
        case 'contains':
          if (Array.isArray(fieldValue)) {
            // Special handling for salesReps array
            if (condition.field === 'salesReps') {
              return fieldValue.some(item => 
                item.salesRepName?.toLowerCase().includes(String(condition.value || '').toLowerCase())
              );
            }
            return fieldValue.some(item => 
              String(item).toLowerCase().includes(String(condition.value || '').toLowerCase())
            );
          }
          return String(fieldValue || '').toLowerCase().includes(String(condition.value || '').toLowerCase());
        case 'not_contains':
          if (Array.isArray(fieldValue)) {
            if (condition.field === 'salesReps') {
              return !fieldValue.some(item => 
                item.salesRepName?.toLowerCase().includes(String(condition.value || '').toLowerCase())
              );
            }
            return !fieldValue.some(item => 
              String(item).toLowerCase().includes(String(condition.value || '').toLowerCase())
            );
          }
          return !String(fieldValue || '').toLowerCase().includes(String(condition.value || '').toLowerCase());
        case 'greater_than':
          return Number(fieldValue) > Number(condition.value);
        case 'less_than':
          return Number(fieldValue) < Number(condition.value);
        case 'after':
          const afterDate = new Date(condition.value);
          const afterFieldDate = fieldValue?.toDate ? fieldValue.toDate() : new Date(fieldValue);
          return afterFieldDate > afterDate;
        case 'before':
          const beforeDate = new Date(condition.value);
          const beforeFieldDate = fieldValue?.toDate ? fieldValue.toDate() : new Date(fieldValue);
          return beforeFieldDate < beforeDate;
        case 'is_true':
          return fieldValue === true;
        case 'is_false':
          return fieldValue === false;
        case 'is_empty':
          if (Array.isArray(fieldValue)) {
            return fieldValue.length === 0;
          }
          return !fieldValue || fieldValue === '';
        case 'is_not_empty':
          if (Array.isArray(fieldValue)) {
            return fieldValue.length > 0;
          }
          return fieldValue && fieldValue !== '';
        default:
          return false;
      }
    });

    // Apply logical operators per condition (same logic as applySegmentRules)
    if (results.length === 0) return false;
    if (results.length === 1) return results[0];
    
    let finalResult = results[0];
    
    for (let i = 1; i < results.length; i++) {
      const condition = rules.conditions[i];
      const logicalOp = condition.logicalOperator || 'AND';
      
      if (logicalOp === 'OR') {
        finalResult = finalResult || results[i];
      } else {
        finalResult = finalResult && results[i];
      }
    }
    
    return finalResult;
  });
}

// Evaluate a single condition (shared by evaluateAllModePreview)
function evaluateCondLocal(fieldValue, operator, condValue, fieldName) {
  switch (operator) {
    case 'equals':
      if (typeof fieldValue === 'string' && typeof condValue === 'string') return fieldValue.toLowerCase() === condValue.toLowerCase();
      return fieldValue === condValue;
    case 'not_equals':
      if (typeof fieldValue === 'string' && typeof condValue === 'string') return fieldValue.toLowerCase() !== condValue.toLowerCase();
      return fieldValue !== condValue;
    case 'contains':
      if (Array.isArray(fieldValue)) {
        if (fieldName === 'salesReps') return fieldValue.some(item => item.salesRepName?.toLowerCase().includes(String(condValue || '').toLowerCase()));
        return fieldValue.some(item => String(item).toLowerCase().includes(String(condValue || '').toLowerCase()));
      }
      return String(fieldValue || '').toLowerCase().includes(String(condValue || '').toLowerCase());
    case 'not_contains':
      if (Array.isArray(fieldValue)) {
        if (fieldName === 'salesReps') return !fieldValue.some(item => item.salesRepName?.toLowerCase().includes(String(condValue || '').toLowerCase()));
        return !fieldValue.some(item => String(item).toLowerCase().includes(String(condValue || '').toLowerCase()));
      }
      return !String(fieldValue || '').toLowerCase().includes(String(condValue || '').toLowerCase());
    case 'greater_than': return Number(fieldValue) > Number(condValue);
    case 'less_than': return Number(fieldValue) < Number(condValue);
    case 'after': {
      const t = new Date(condValue), f = fieldValue?.toDate ? fieldValue.toDate() : new Date(fieldValue);
      return f > t;
    }
    case 'before': {
      const t = new Date(condValue), f = fieldValue?.toDate ? fieldValue.toDate() : new Date(fieldValue);
      return f < t;
    }
    case 'is_empty':
      if (Array.isArray(fieldValue)) return fieldValue.length === 0;
      return !fieldValue || fieldValue === '';
    case 'is_not_empty':
      if (Array.isArray(fieldValue)) return fieldValue.length > 0;
      return fieldValue && fieldValue !== '';
    default: return false;
  }
}

// Cross-collection evaluation for "All" mode preview
function evaluateAllModePreview(contacts, customers, opportunities, rules) {
  if (!rules?.conditions?.length) return contacts.filter(c => c.contactEmail && validateEmail(c.contactEmail));

  // Build reverse lookup: contact → linked opportunities
  const oppsByContactEmail = new Map();
  const oppsByContactId = new Map();
  opportunities.forEach(opp => {
    if (opp.contactEmail) {
      const key = opp.contactEmail.toLowerCase();
      if (!oppsByContactEmail.has(key)) oppsByContactEmail.set(key, []);
      oppsByContactEmail.get(key).push(opp);
    }
    if (opp.contactId) {
      if (!oppsByContactId.has(opp.contactId)) oppsByContactId.set(opp.contactId, []);
      oppsByContactId.get(opp.contactId).push(opp);
    }
  });

  return contacts.filter(contact => {
    if (!contact.contactEmail || !validateEmail(contact.contactEmail)) return false;

    const customer = contact.customer || {};

    // Get linked opportunities (by email or contactId), deduplicated
    const byEmail = oppsByContactEmail.get(contact.contactEmail.toLowerCase()) || [];
    const byId = oppsByContactId.get(contact.id) || [];
    const seenIds = new Set();
    const linkedOpps = [...byEmail, ...byId].filter(o => {
      if (seenIds.has(o.id)) return false;
      seenIds.add(o.id);
      return true;
    });

    // Evaluate each condition against the appropriate collection
    const results = rules.conditions.map(condition => {
      const sepIdx = condition.field.indexOf('__');
      if (sepIdx === -1) return false;
      const source = condition.field.substring(0, sepIdx);
      const actualField = condition.field.substring(sepIdx + 2);

      if (source === 'customer') {
        return evaluateCondLocal(getNestedValue(customer, actualField), condition.operator, condition.value, actualField);
      } else if (source === 'contact') {
        return evaluateCondLocal(getNestedValue(contact, actualField), condition.operator, condition.value, actualField);
      } else if (source === 'opp') {
        // For opportunity conditions: match if ANY linked opportunity satisfies
        if (linkedOpps.length === 0) return condition.operator === 'is_empty';
        if (condition.operator === 'is_empty') {
          return linkedOpps.every(o => {
            const fv = getNestedValue(o, actualField);
            return !fv || (Array.isArray(fv) && fv.length === 0);
          });
        }
        if (condition.operator === 'is_not_empty') {
          return linkedOpps.some(o => {
            const fv = getNestedValue(o, actualField);
            return fv && (!Array.isArray(fv) || fv.length > 0);
          });
        }
        return linkedOpps.some(o => evaluateCondLocal(getNestedValue(o, actualField), condition.operator, condition.value, actualField));
      }
      return false;
    });

    // Apply per-condition AND/OR chaining
    if (results.length === 0) return false;
    if (results.length === 1) return results[0];

    let finalResult = results[0];
    for (let i = 1; i < results.length; i++) {
      const logicalOp = rules.conditions[i].logicalOperator || 'AND';
      finalResult = logicalOp === 'OR' ? (finalResult || results[i]) : (finalResult && results[i]);
    }
    return finalResult;
  });
}

export default SegmentBuilder;



